import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ref, set, onValue } from "firebase/database";
import { onAuthStateChanged, User } from "firebase/auth";
import { database, auth } from "../config/firebase";
import { signOut } from 'firebase/auth';
import i18n from '../i18n';

export enum AppState {
  HOME = 'home',
  SCREEN = 'screen',
  SELECT_GROUP = 'select-group',
  WAITING_FORMATION = 'waiting-formation',
  TRAINING = 'training',
  SLIDE_CONFIRMATION = 'slide-confirmation',
  DESIGN = 'design',
  DESIGN_DUMMY = 'design-dummy',
  PITCH = 'pitch',
  PITCH_DUMMY = 'pitch-dummy',
  THANK_YOU = 'thank-you',
  SURVEY = 'survey',
  VIDEO = 'video',
  // Not used for now:
  SETTINGS = 'settings',
  EVALUATION1 = 'evaluation1',
  TOOLS = 'tools',
  CLOSING_REMARKS = 'closing-remarks',
}

interface NavigationContextType {
  currentStep: AppState;
  runState: (step: AppState | string, payload?: any) => void;
  waitingForStatus: boolean;
  user: User | null;
  stepData: any;
  workshopId: string | undefined;
}

const NavigationContext = createContext<NavigationContextType | undefined>(undefined);

const STORAGE_KEY = 'app_current_step';

export const performLogout = async (callback?: () => void) => {
  try {
    await signOut(auth);
  } catch (error) {
    console.error("Error signing out:", error);
  } finally {
    if (callback) callback();
  }
};

export const NavigationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [currentStep, setCurrentStep] = useState<AppState>(() => {
    const savedStep = localStorage.getItem(STORAGE_KEY);
    return (savedStep as AppState) || AppState.HOME;
  });
  const navigate = useNavigate();
  const location = useLocation();
  const [waitingForStatus, setWaitingForStatus] = useState(false);
  const [user, setUser] = useState<User | null>(auth?.currentUser || null);
  const [stepData, setStepData] = useState<any>(null);

  const lastActionTimestamp = useRef<number>(0);

  useEffect(() => {
    if (!auth) return;
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      user ? console.log("logged in") : console.log("logged out");

    });
    return () => unsubscribe();
  }, [user]);

  // Extract workshopId from the URL (e.g., /workshop/123/... or /workshop-d/123/...)
  const getWorkshopId = (pathname: string) => {
    const match = pathname.match(/\/workshop(?:-d)?\/([^/]+)/);
    return match ? match[1] : undefined;
  };
  const workshopId = getWorkshopId(location.pathname);
  const userId = user?.uid;

  useEffect(() => {
    if (!waitingForStatus || !database || !workshopId || !userId) return;

    const statusRef = ref(database, `/workshops/${workshopId}/${userId}/status`);
    const unsubscribe = onValue(statusRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        console.log("runState response", data);
        const { nextState, timestamp, ...rest } = data;

        // Verify if the response corresponds to the latest action
        if (timestamp && timestamp !== lastActionTimestamp.current) {
          console.warn("Received response for an old action", { timestamp, lastActionTimestamp: lastActionTimestamp.current });
          return;
        }

        setWaitingForStatus(false);

        if (nextState === "thank-you") {
          performLogout();
        }

        if (location.pathname.includes("-d")) {
          let route = nextState;
          if (rest.stateType === 'survey') {
            route = `survey/${nextState}`;
          } else if (rest.stateType === 'video') {
            route = `video/${nextState}`;
          }
          navigate(`/workshop-d/${workshopId}/${route}?lang=${i18n.language}`, { state: rest });
        } else {
          setCurrentStep(nextState as AppState);
          setStepData(rest);
          window.scrollTo(0, 0);
        }
      }
    });

    return () => unsubscribe();
  }, [waitingForStatus, workshopId, userId, navigate, location.pathname]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, currentStep);
  }, [currentStep]);

  const runState = (step: AppState | string, payload: any = {}) => {
    const currentUserId = userId || auth?.currentUser?.uid;

    console.log("runState", step, payload);

    if (!(workshopId && currentUserId && database)) {
      console.error("runState called but missing workshopId/userId/database context", { workshopId, userId: currentUserId, database: !!database });
    } else {
      const timestamp = Date.now();
      lastActionTimestamp.current = timestamp;
      setWaitingForStatus(true);
      set(ref(database, `/workshops/${workshopId}/${currentUserId}/action`), {
        actionName: step, // "step" here acts as the CURRENT state/action
        timestamp,
        debug: location.pathname.includes("-d"),
        lang: i18n.language,
        ...payload
      }).catch(e => {
        console.error("Error setting action:", e);
        setWaitingForStatus(false);
      });
    }
  }

  return (
    <NavigationContext.Provider value={{ currentStep, runState, waitingForStatus, user, stepData, workshopId }}>
      {children}
    </NavigationContext.Provider>
  );
};

export const useNavigation = () => {
  const context = useContext(NavigationContext);
  if (context === undefined) {
    throw new Error('useNavigation must be used within a NavigationProvider');
  }
  return context;
};